import React, { ReactNode } from 'react';
import styled from 'styled-components';

import { ArticleOutlined } from '@mui/icons-material';
import { Stack, alpha } from '@mui/material';
import { Button } from '../';
import { useTheme } from '../../styles';

const Container = styled.div<{ containerWidth?: string; containerHeight?: string }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  width: ${({ containerWidth }) => containerWidth || 'calc(100vw - 61px - 240px - 48px)'};
  height: ${({ containerHeight }) => containerHeight || 'calc(100vh - 61px)'};

  & .MuiButton-startIcon {
    svg {
      font-size: 2ch !important;
    }
    min-width: 14px;
  }
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;
  align-items: center;
  padding: 7rem 12rem;
  margin-bottom: 4rem;
  max-width: 600px;
  min-height: 300px;
  gap: 1.5rem;
  border: 4px dashed ${({ theme }) => alpha(theme.line.light, 0.65)};
  border-radius: 48px;

  h1 {
    color: ${({ theme }) => theme.text.standard};
  }

  p {
    color: ${({ theme }) => theme.text.standard};
  }

  svg {
    color: ${({ theme }) => theme.primary};
    font-size: 4ch;
  }

  .icon {
    display: flex;
    align-items: center;
    justify-content: center;

    width: 40px;
    height: 40px;
  }
`;

interface Props {
  resourceName: string;
  resourceInfo: string;
  resourceIcon: ReactNode;
  resourceButtonIcon?: ReactNode;
  thirdButton?: ReactNode;
  onClickGuide: () => void;
  onClickCreate: () => void;
  createDisabled?: boolean;
  containerWidth?: string;
  containerHeight?: string;
}

export const ResourceFallback: React.FC<Props> = ({
  resourceName,
  resourceInfo,
  resourceIcon,
  resourceButtonIcon,
  thirdButton,
  createDisabled = false,
  onClickGuide,
  onClickCreate,
  containerWidth,
  containerHeight,
}) => {
  const theme = useTheme();
  return (
    <Container containerWidth={containerWidth} containerHeight={containerHeight}>
      <Card>
        <div className='icon'>{resourceIcon}</div>
        <h1>Create your first {resourceName.toLowerCase()}</h1>
        <p>{resourceInfo}</p>
        <Stack direction='row' spacing={1} marginTop={2}>
          <Button
            startIcon={<ArticleOutlined sx={{ color: `${theme.text.standard} !important` }} />}
            sx={{ marginTop: '2rem' }}
            onClick={onClickGuide}
          >
            {`${resourceName} Guide`}
          </Button>
          <Button
            data-intercom-target='create-resource-button'
            disabled={createDisabled}
            startIcon={resourceButtonIcon || resourceIcon}
            sx={{ marginTop: '2rem', svg: { color: 'white' } }}
            customColor='primary'
            onClick={onClickCreate}
          >
            {`Create ${resourceName}`}
          </Button>
          {thirdButton && thirdButton}
        </Stack>
      </Card>
    </Container>
  );
};
